/**
 * Centralized API service with credentials enabled for all requests
 */

const API_BASE_URL = '/api';

interface ApiOptions extends RequestInit {
  headers?: Record<string, string>;
}

class ApiService {
  private getDefaultHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    // Include JWT auth token if available
        const token = typeof window !== 'undefined' ? (localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')) : null;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      ...options,
      credentials: 'include', // Send cookies with every request
      headers: {
        ...this.getDefaultHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // ── Global 401 handler: token expired or invalid ─────────────
      if (response.status === 401) {
        // Purge ALL token storage immediately — both localStorage and sessionStorage.
        // login() writes to both on success, but a stale token from before the dual-write
        // fix (June 2026) could still be sitting in one storage with priority over the other.
        // Clearing both ensures the next mount starts fresh.
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user')
          localStorage.removeItem('user_role')
          localStorage.removeItem('user_email')
          sessionStorage.clear()
          window.dispatchEvent(new CustomEvent('auth:unauthorized'))
        }
        const errorData = await response.json().catch(() => ({
          detail: `Request failed with status ${response.status}`,
        }));
        throw new Error(errorData.detail || errorData.message || `API error: ${response.status}`);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `Request failed with status ${response.status}`,
        }));
        throw new Error(errorData.detail || errorData.message || `API error: ${response.status}`);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return {} as T;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // HTTP methods
  async get<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
        const token = typeof window !== 'undefined' ? (localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')) : null;

    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: `Upload failed with status ${response.status}`,
      }));
      throw new Error(errorData.detail || `Upload error: ${response.status}`);
    }

    return await response.json();
  }

  // Auth-specific methods
  async login(email: string, password: string) {
    return this.post<{ user: any; token?: string }>('/auth/login/', { email, password });
  }

  async logout() {
    return this.post('/auth/logout/');
  }

  async getCurrentUser() {
    return this.get('/users/me/');
  }

  async validateSession() {
    return this.get('/users/me/');
  }
}

// Export singleton instance
export const api = new ApiService();